















































import { defineComponent, toRefs, ref, Ref, watch } from '@vue/composition-api';
import axios from 'axios';
import OlbButton from '../button/button.vue';
import OlbIcon from '../icon/icon.vue';
export type OlbFile = {
	name: string;
	size: string;
	progress?: number;
};
/**
 * Upload component
 */
export default defineComponent({
	components: {
		OlbButton,
		OlbIcon,
	},
	props: {
		/**
		 * Maximum files a user can upload
		 */
		maxFiles: {
			type: Number,
			default: 10,
		},
		/**
		 * Maximum size per file in megabytes a user can upload
		 */
		maxSize: {
			type: Number,
			default: 10000,
		},
		/**
		 * Endpoint to upload files to directly after selection
		 */
		uploadApi: {
			type: String,
		},
		/**
		 * Optional formData name for direct upload and field names
		 */
		formDataName: {
			type: String,
		},
		/**
		 * Comma delimited file-extensions
		 */
		accept: {
			type: String,
		},
	},
	setup(props, { emit, slots }) {
		const { maxFiles, maxSize, uploadApi, formDataName, accept } = toRefs(props);
		const files: Ref<OlbFile[]> = ref([]);
		const uploadComponent: Ref<HTMLDivElement | null> = ref(null);
		const syntheticFileList = ref<File[]>([]);

		emit('maxfiles', false);

		function formatBytes(bytes: number, decimals = 2) {
			if (bytes === 0) return '0 bytes';

			const k = 1024;
			const dm = decimals < 0 ? 0 : decimals;
			const sizes = ['bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb'];

			const i = Math.floor(Math.log(bytes) / Math.log(k));

			return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
		}

		function maxFilesReached() {
			return files.value.length >= maxFiles.value;
		}

		function maxSizeReached(fileSize: number) {
			return maxSize?.value && maxSize.value <= fileSize / 1048576;
		}

		function removeFile(fileName: string) {
			files.value = files.value.filter((file) => file.name !== fileName);
			syntheticFileList.value = syntheticFileList.value.filter((file) => file.name !== fileName);
			emit('maxfiles', maxFilesReached());
		}

		function duplicateFile(fileName: string) {
			return files.value.filter((file) => file.name === fileName).length > 0;
		}

		function invalidFile(fileName: string) {
			const extension = fileName.substr(fileName.lastIndexOf('.'));
			if (accept?.value) {
				return !accept.value.includes(extension);
			}
			return false;
		}

		function onFileChange(fileList: FileList, target: EventTarget) {
			Array.from(fileList).map((file) => {
				emit('invalid', invalidFile(file.name) ? file.name : false);
				emit('maxfiles', maxFilesReached());
				emit('maxsize', maxSizeReached(file.size));
				if (
					duplicateFile(file.name) ||
					invalidFile(file.name) ||
					maxFilesReached() ||
					maxSizeReached(file.size)
				) {
					return;
				}

				syntheticFileList.value.push(file);

				files.value.push({
					name: file.name,
					size: formatBytes(file.size),
					progress: 0,
				});

				/**
				 * clear input file value to avoid error
				 * https://jira.namics.com/browse/OLBFB6-2263
				 */
				(target as HTMLInputElement).value = '';

				if (uploadApi?.value) {
					const formData = new FormData();
					formData.append(formDataName?.value as string, file);
					axios.post(uploadApi.value, formData, {
						headers: {
							'content-type': 'multipart/form-data',
						},
						onUploadProgress: (progressEvent) => {
							const foundFile = files.value.find((needle) => needle.name === file.name);
							if (foundFile) {
								foundFile.progress = (progressEvent.loaded * 100) / progressEvent.total;
							}
						},
					});
				}
			});
		}

		watch(syntheticFileList, () => {
			// necessary to dispatch JS event due to listening in form.js
			let fileSelectedEvent;
			// if phrase necessary due to IE11 compatibility (see https://stackoverflow.com/questions/27176983/dispatchevent-in-chrome-but-not-in-ie11)
			if (typeof CustomEvent === 'function') {
				fileSelectedEvent = new CustomEvent('selected', { detail: syntheticFileList.value });
			} else {
				fileSelectedEvent = document.createEvent('CustomEvent');
				fileSelectedEvent.initCustomEvent('selected', true, true, syntheticFileList);
			}
			uploadComponent?.value?.dispatchEvent(fileSelectedEvent);
			emit('selected', syntheticFileList.value);
		});

		return {
			onFileChange,
			files,
			removeFile,
			maxFilesReached,
			slots,
			uploadComponent,
		};
	},
});
